import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { DialogDocumentsAddMultipleConfirm } from 'app/dialogs/documents/documents-add-multiple-confirm/dialog-documents-add-multiple-confirm';
import { subtractYears } from 'app/helpers/subtractYears';
import { DocumentsDivisionDefaultValues } from 'app/interfaces/documents/documentsDivisionDefaultValues';
import { PensionSummaryUploadMessageTemplateData } from 'app/interfaces/documents/pensionSummaryUploadMessageTemplateData';
import { DropDownItem } from 'app/interfaces/items/dropDownItem';
import { ExposedCreateDocumentGroupReq } from 'app/interfaces/requests/Exposed/exposedCreateDocumentGroupReq';
import { ExposedUploadGroupDocumentFileReq } from 'app/interfaces/requests/Exposed/exposedUploadGroupDocumentFileReq';
import { ExposedReadUserDetailsRes } from 'app/interfaces/responses/Exposed/exposedReadUserDetailsRes';
import { GroupsService } from 'app/services/api/groups/groups.service';
import { LsrauthService } from 'app/services/api/lsrauth/lsrauth.service';
import { SkjalamidjaService } from 'app/services/api/skjalamidja/skjalamidja.service';
import { TranslationService } from 'app/services/api/translation/translation.service';
import { AlertsService } from 'app/services/system/alerts/alerts.service';
import { DOCUMENT_TYPES } from 'common/constants/document-type.constants';
import { USER_ROLES } from 'common/constants/user-role.constants';
import moment from 'moment';
import {
    DocumentsAddGeneralInfoComponent,
    DocumentsAddGeneralInfoValue,
} from '../../../documents-add-general-info/documents-add-general-info.component';

@Component({
    selector: 'documents-add-multiple-system-portal-lsr',
    templateUrl: './documents-add-multiple-system-portal-lsr.component.html',
})
export class DocumentsAddMultipleSystemPortalLsrComponent implements OnInit {
    //#region Globals

    @ViewChild(MatAutocompleteTrigger) autocomplete: MatAutocompleteTrigger;
    @ViewChild(DocumentsAddGeneralInfoComponent)
    generalInfoComponent: DocumentsAddGeneralInfoComponent;
    userRoles = USER_ROLES;
    userDetails: ExposedReadUserDetailsRes = null;

    form: FormGroup;
    formEnabled = false;
    submitted = false;
    files: File[] = [];
    pdfFiles = true;

    documentTemplatesDropDown: DropDownItem[] = [];
    groupsDropDown: DropDownItem[] = [];
    showAdditionalInputs: boolean = false;

    //#endregion

    //#region Constructor and init

    constructor(
        private route: ActivatedRoute,
        private formBuilder: FormBuilder,
        private skjalamidja: SkjalamidjaService,
        private alertsService: AlertsService,
        private groupsService: GroupsService,
        private translationService: TranslationService,
        private router: Router,
        protected lsrauthService: LsrauthService,
        public dialog: MatDialog
    ) {
        this.lsrauthService.userDetails.subscribe((userDetails) => (this.userDetails = userDetails));
    }

    ngOnInit(): void {
        this.initForm();
        this.initDropdowns();
    }

    initDropdowns() {
        const { dropdownData } = this.route.snapshot.parent.data;
        const [categoriesRes, subcategoriesRes, systemsRes, typesRes, usersRes, documentTemplates] = dropdownData;

        const snjallariLSR = systemsRes.find((x) => x.title === 'Snjallari LSR');
        this.form.get('documentSystemId').setValue(snjallariLSR.documentSystemId);

        this.documentTemplatesDropDown = documentTemplates.map((x) => ({
            value: x.templateKey.toString(),
            viewValue: x.templateTranslationKey,
        }));

        this.groupsService.listUserGroups().subscribe((res) => {
            this.groupsDropDown = res.map((group) => ({
                value: group.groupId,
                viewValue: group.name,
            }));
        });
    }

    initForm() {
        this.submitted = false;
        this.form = this.formBuilder.group({
            generalInfo: new FormControl(''),
            group: new FormControl(null, Validators.required),
            documentSystemId: new FormControl(null, Validators.required),
            documentType: new FormControl('file', Validators.required),
            documentTemplateKey: new FormControl(''),
            documentTemplatePaymentFromDate: new FormControl(subtractYears(new Date(), 1)),
            documentTemplatePaymentToDate: new FormControl(new Date()),
            documentTemplateSalaryFromDate: new FormControl(''),
            documentTemplateSalaryToDate: new FormControl(''),
            documentTemplateLetterMail: new FormControl(false),
            includeAdditionalDocument: new FormControl(false),
        });
        this.form.markAsPristine();
        this.formEnabled = true;

        // Additional input fields should only be shown for pension overview document types
        this.form.get('documentTemplateKey').valueChanges.subscribe((value) => {
            var types = [
                DOCUMENT_TYPES.DOC_TYPE_PENSION_A_DEILD,
                DOCUMENT_TYPES.DOC_TYPE_PENSION_B_DEILD,
                DOCUMENT_TYPES.DOC_TYPE_PENSION_RECIPIENT,
                DOCUMENT_TYPES.DOC_TYPE_PENSION_SEREIGN,
            ];
            this.showAdditionalInputs = types.includes(value);
        });
    }

    //#endregion

    //#region Save / Submit

    confirmAndSave() {
        let templateName = null;
        if (this.form.get('documentTemplateKey').value) {
            templateName = this.translationService.translate(
                this.documentTemplatesDropDown.find((x) => x.value == this.form.get('documentTemplateKey').value).viewValue
            );
        }

        let groupName = this.groupsDropDown.find((x) => x.value == this.form.get('group').value).viewValue;

        const dialogRef = this.dialog.open(DialogDocumentsAddMultipleConfirm, {
            autoFocus: false,
            restoreFocus: false,
            width: '600px',
            data: {
                groupName,
                fileName: this.files[0]?.name ?? null,
                templateName: templateName,
                documentType: this.form.get('documentType').value,
            },
        });
        dialogRef.afterClosed().subscribe((confirmed) => {
            if (confirmed) {
                this.save();
            }
        });
    }

    save() {
        this.generalInfoComponent.form.markAllAsTouched();
        this.submitted = true;

        if (!this.form.valid) {
            this.alertsService.changealertHeader('Villur í formi');
            this.alertsService.changeAlertErrorMessage('Vinsamlegast leysið úr villum í formi');
            this.alertsService.changeAlertErrorShow(true);
            return;
        }

        this.form.disable();
        this.formEnabled = false;
        this.storeSelectedValues();

        const generalInfo = this.generalInfoComponent.form.value as DocumentsAddGeneralInfoValue;

        if (this.form.get('documentType').value == 'template') {
            const req: ExposedCreateDocumentGroupReq = this.buildTemplateRequest(generalInfo);
            this.skjalamidja.uploadDocumentGroupTemplate(req, this.files, false).subscribe({
                next: (res) => {
                    if (res) {
                        this.initForm();
                        this.router.navigate(['/skjalasafn/skoda-bunka-vinnslur']);
                    } else {
                        this.alertsService.changealertHeader('Error');
                        this.alertsService.changeAlertErrorMessage('Villa kom upp við að senda gögn');
                        this.alertsService.changeAlertErrorShow(true);
                        this.form.enable();
                        this.formEnabled = true;
                    }
                },
            });
        } else {
            const req: ExposedUploadGroupDocumentFileReq = this.buildFileRequest(generalInfo);
            this.skjalamidja.uploadDocumentGroupFile(req, this.files[0], false).subscribe({
                next: (res) => {
                    if (res) {
                        this.initForm();
                        this.router.navigate(['/skjalasafn/skoda-bunka-vinnslur']);
                    } else {
                        this.alertsService.changealertHeader('Error');
                        this.alertsService.changeAlertErrorMessage('Villa kom upp við að senda gögn');
                        this.alertsService.changeAlertErrorShow(true);
                        this.form.enable();
                        this.formEnabled = true;
                    }
                },
            });
        }
    }

    //#endregion

    //#region File selection

    onSelect(event) {
        this.files.push(...event.addedFiles);
        if (this.files.length > 1) {
            this.files.splice(0, 1);
        }
        if (this.files[0].name.length <= 80 && this.form.get('documentType').value == 'file') {
            this.form.patchValue({
                generalInfo: {
                    ...this.form.get('generalInfo').value,
                    title: this.files[0].name,
                },
            });
        }
    }

    onRemove(event) {
        this.files.splice(this.files.indexOf(event), 1);
    }

    checkIfPdfFiles() {
        this.form.patchValue({
            generalInfo: {
                ...this.form.get('generalInfo').value,
                submitToStafraentIsland: false,
            },
        });
        this.pdfFiles = true;
        this.files.forEach((x) => {
            if (x.type != 'application/pdf') {
                this.pdfFiles = false;
            }
        });
    }

    //#endregion

    //#region Helper methods

    buildTemplateRequest(generalInfo: DocumentsAddGeneralInfoValue): ExposedCreateDocumentGroupReq {
        return {
            documentTemplateKey: this.form.get('documentTemplateKey').value,
            groupId: this.form.get('group').value,
            title: generalInfo.title,
            description: generalInfo.description,
            documentDate: generalInfo.documentDate,
            documentTypeId: generalInfo.documentTypeId,
            documentCategoryId: generalInfo.documentCategoryId,
            documentSubcategoryId: generalInfo.documentSubcategoryId,
            documentSystemId: this.form.get('documentSystemId').value,
            publicationDate: generalInfo.publicationDate,
            svid: generalInfo.svid,
            deild: generalInfo.deild,
            submitToStafraentIsland: generalInfo.submitToStafraentIsland || false,
            submitToPortalLSR: generalInfo.submitToPortalLSR,
            templateData: {
                letterMail: this.form.get('documentTemplateLetterMail').value,
                paymentFromDate: moment(this.form.get('documentTemplatePaymentFromDate').value).format('YYYY-MM-DD'),
                paymentToDate: moment(this.form.get('documentTemplatePaymentToDate').value).format('YYYY-MM-DD'),
                salaryFromDate: this.form.get('documentTemplateSalaryFromDate').value
                    ? moment(this.form.get('documentTemplateSalaryFromDate').value).format('YYYY-MM-DD')
                    : null,
                salaryToDate: this.form.get('documentTemplateSalaryToDate').value
                    ? moment(this.form.get('documentTemplateSalaryToDate').value).format('YYYY-MM-DD')
                    : null,
                showSalaryDate: !!(
                    this.form.get('documentTemplateSalaryToDate').value && this.form.get('documentTemplateSalaryFromDate').value
                ),
            } as PensionSummaryUploadMessageTemplateData,
        };
    }

    buildFileRequest(generalInfo: DocumentsAddGeneralInfoValue): ExposedUploadGroupDocumentFileReq {
        return {
            groupId: this.form.get('group').value,
            title: generalInfo.title,
            description: generalInfo.description,
            documentDate: generalInfo.documentDate,
            documentTypeId: generalInfo.documentTypeId,
            documentCategoryId: generalInfo.documentCategoryId,
            documentSubcategoryId: generalInfo.documentSubcategoryId,
            documentSystemId: this.form.get('documentSystemId').value,
            publicationDate: generalInfo.publicationDate,
            svid: generalInfo.svid,
            deild: generalInfo.deild,
            submitToStafraentIsland: generalInfo.submitToStafraentIsland || false,
            submitToPortalLSR: generalInfo.submitToPortalLSR,
        } as ExposedUploadGroupDocumentFileReq;
    }

    revertSingleDocAllChanges() {
        if (confirm('Ertu viss um að þú viljir hætta við?')) {
            this.initForm();
        }
    }

    storeSelectedValues() {
        const values: DocumentsDivisionDefaultValues = {
            documentType: this.generalInfoComponent.form.get('documentTypeId').value,
            documentSystem: this.form.get('documentSystemId').value,
            documentCategory: this.generalInfoComponent.form.get('documentCategoryId').value,
            documentSubcategory: this.generalInfoComponent.form.get('documentSubcategoryId').value,
            division: this.generalInfoComponent.form.get('svid').value,
            department: this.generalInfoComponent.form.get('deild').value,
            defaultValueSelection: this.generalInfoComponent.form.get('defaultValue').value,
        };
        localStorage.setItem('documentsDivisionDefaultValues', JSON.stringify(values));
    }

    changeDocumentType(event) {
        this.files = [];
        if (event.value == 'template') {
            this.form.get('documentTemplateKey').setValidators(Validators.required);
            this.form.get('documentTemplateKey').updateValueAndValidity();
            this.form.get('documentTemplatePaymentFromDate').setValidators(Validators.required);
            this.form.get('documentTemplatePaymentFromDate').updateValueAndValidity();
            this.form.get('documentTemplatePaymentToDate').setValidators(Validators.required);
            this.form.get('documentTemplatePaymentToDate').updateValueAndValidity();
        } else {
            this.form.get('documentTemplateKey').clearValidators();
            this.form.get('documentTemplateKey').updateValueAndValidity();
            this.form.get('documentTemplatePaymentFromDate').clearValidators();
            this.form.get('documentTemplatePaymentFromDate').updateValueAndValidity();
            this.form.get('documentTemplatePaymentToDate').clearValidators();
            this.form.get('documentTemplatePaymentToDate').updateValueAndValidity();
        }
    }

    //#endregion
}
